import { z } from 'zod';
/**
 * ConversationUI Form Schema
 *
 * Defines the structure for interactive UI prompts that agents can send
 * to collect structured user input within conversations.
 */
// Field option schema - for choice-based fields
export const FieldOptionSchema = z.object({
    value: z.string(),
    label: z.string(),
    icon: z.string().optional(),
    description: z.string().optional()
});
// Base field schema - common properties for all field types
const BaseFieldSchema = z.object({
    id: z.string(),
    type: z.enum([
        'singleChoice',
        'multiChoice',
        'dateTime',
        'textInput',
        'number'
    ]),
    label: z.string(),
    description: z.string().optional(),
    required: z.boolean().default(false),
    placeholder: z.string().optional()
});
// Single choice field - radio buttons
export const SingleChoiceFieldSchema = BaseFieldSchema.extend({
    type: z.literal('singleChoice'),
    options: z.array(FieldOptionSchema).min(1),
    showMore: z.boolean().optional(),
    defaultValue: z.string().optional() // Default selected option value
});
// Multi choice field - checkboxes
export const MultiChoiceFieldSchema = BaseFieldSchema.extend({
    type: z.literal('multiChoice'),
    options: z.array(FieldOptionSchema).min(1),
    min: z.number().optional(),
    max: z.number().optional(),
    showMore: z.boolean().optional(),
    displayMode: z.enum(['list', 'grid']).optional() // Display as list (default) or grid of cards
});
// Date/time field - date and/or time picker
export const DateTimeFieldSchema = BaseFieldSchema.extend({
    type: z.literal('dateTime'),
    includeTime: z.boolean().default(false),
    minDate: z.string().optional(),
    maxDate: z.string().optional(),
    allowUnknown: z.boolean().default(false)
});
// Text input field - single or multi-line text
export const TextInputFieldSchema = BaseFieldSchema.extend({
    type: z.literal('textInput'),
    multiline: z.boolean().default(false),
    maxLength: z.number().optional()
});
// Number input field - numeric input
export const NumberFieldSchema = BaseFieldSchema.extend({
    type: z.literal('number'),
    min: z.number().optional(),
    max: z.number().optional(),
    step: z.number().optional()
});
// Union of all field types using discriminated union
export const FormFieldSchema = z.discriminatedUnion('type', [
    SingleChoiceFieldSchema,
    MultiChoiceFieldSchema,
    DateTimeFieldSchema,
    TextInputFieldSchema,
    NumberFieldSchema
]);
// Button configuration
export const ButtonConfigSchema = z.object({
    label: z.string(),
    variant: z.enum(['default', 'outline', 'ghost', 'destructive']).optional()
});
// Complete form schema
// NOTE: Agent-generated forms are restricted to exactly ONE field (Typeform style)
// Workflow steps can have multiple fields (see WorkflowStepSchema.fields in workflows/schemas/workflow.schema.ts)
export const FormUISchema = z.object({
    type: z.literal('form'),
    question: z.string().min(1), // Required question that appears prominently in the UI
    title: z.string().optional(),
    description: z.string().optional(),
    fields: z.array(FormFieldSchema).min(1), // Agent forms: exactly 1 field enforced at runtime. Workflow steps: can be multiple fields
    buttons: z.object({
        submit: ButtonConfigSchema.optional(),
        cancel: ButtonConfigSchema.optional()
    }).optional(),
    metadata: z.object({
        flowId: z.string().optional(),
        step: z.number().optional(),
        totalSteps: z.number().optional()
    }).optional()
});
//# sourceMappingURL=form.schema.js.map